import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Grid,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { authService } from '../services/auth';
import { getPortalAccess, resolveProviderId } from '../utils/portalAccess';
import { Client, ClientCreate, ClientUpdate, OhioCounty, Provider } from '../types';
import AppDateField from '../components/AppDateField';
import AddressFields, { AddressValue } from '../components/AddressFields';
import { fieldOnPageBlueSx } from '../mui/fieldOnPageBlue';



interface ClientsProps {
  selectedProvider?: number | null;
}

const Clients: React.FC<ClientsProps> = ({ selectedProvider }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerFilter, setProviderFilter] = useState<Provider | null>(null);
  const [providerFilterInput, setProviderFilterInput] = useState('');
  const [clientFilter, setClientFilter] = useState<Client | null>(null);
  const [clientFilterInput, setClientFilterInput] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const appliedHeaderProvider = useRef<number | null | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientSaveAttempted, setClientSaveAttempted] = useState(false);
  const [totalClients, setTotalClients] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requiresProviderSelection, setRequiresProviderSelection] = useState(false);
  const [ohioCounties, setOhioCounties] = useState<OhioCounty[]>([]);


  const fetchClients = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const access = await getPortalAccess();
      setIsAdmin(access.isAdmin);
      setRequiresProviderSelection(access.requiresProviderSelection);

      const providerResponse = await api.providers.getAll();
      const providerList: Provider[] = Array.isArray(providerResponse) ? providerResponse : [];
      setProviders(providerList);

      let clientList: Client[] = [];
      if (access.isAdmin) {
        const response = await api.clients.list(0, 5000);
        clientList = Array.isArray(response) ? response : [];
      } else {
        const providerIds = providerList
          .map((provider) => Number(provider.provider_id))
          .filter((id) => id > 0);
        const batches = await Promise.all(
          providerIds.map((id) => api.clients.listByProvider(id, 1000))
        );
        const seen = new Set<string>();
        clientList = batches.flat().filter((client: Client) => {
          const key = String(client.client_id);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      setClients(clientList);
      setTotalClients(clientList.length);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [selectedProvider, fetchClients]);

  useEffect(() => {
    const fetchOhioCounties = async () => {
      try {
        const counties = await api.ohioCounty.getAll();
        setOhioCounties(counties);
      } catch (error) {
        console.error('Error fetching Ohio counties:', error);
      }
    };
    fetchOhioCounties();
  }, []);

  useEffect(() => {
    if (appliedHeaderProvider.current === selectedProvider) return;
    if (selectedProvider != null && providers.length === 0) return;

    appliedHeaderProvider.current = selectedProvider ?? null;
    setClientFilter(null);
    setClientFilterInput('');

    if (selectedProvider == null) {
      setProviderFilter(null);
      setProviderFilterInput('');
      return;
    }

    const match = providers.find((provider) => Number(provider.provider_id) === Number(selectedProvider)) ?? null;
    setProviderFilter(match);
    setProviderFilterInput(match ? `${match.first_name} ${match.last_name}`.trim() : '');
  }, [selectedProvider, providers]);

  const handleSave = async () => {
    if (!selectedClient) return;

    const firstNameMissing = !selectedClient.first_name?.trim();
    const lastNameMissing = !selectedClient.last_name?.trim();
    if (firstNameMissing || lastNameMissing) {
      setClientSaveAttempted(true);
      setError('First name and last name are required.');
      return;
    }
    setClientSaveAttempted(false);

    try {
      setIsLoading(true);
      setError(null);
      if (selectedClient.client_id) {
        const updateData: ClientUpdate = {
          first_name: selectedClient.first_name,
          last_name: selectedClient.last_name,
          medicaid_num: selectedClient.medicaid_num,
          first_initial: selectedClient.first_initial,
          date_of_birth: selectedClient.date_of_birth,
          gender: selectedClient.gender,
          phone: selectedClient.phone,
          email: selectedClient.email,
          address_line1: selectedClient.address_line1,
          address_line2: selectedClient.address_line2,
          city: selectedClient.city,
          county_id: selectedClient.county_id,
          state: selectedClient.state,
          zip_code: selectedClient.zip_code,
          emergency_contact_name: selectedClient.emergency_contact_name,
          emergency_contact_phone: selectedClient.emergency_contact_phone,
          goals: selectedClient.goals,
          personal_care_plan: selectedClient.personal_care_plan,
          allergies: selectedClient.allergies,
          notes: selectedClient.notes,
          authorized_service_codes: selectedClient.authorized_service_codes,
          isp: selectedClient.isp,
          favorite_locations: selectedClient.favorite_locations,
          complex_care_flag: selectedClient.complex_care_flag || false,
          behavioral_support_flag: selectedClient.behavioral_support_flag || false,
          medical_assistance_flag: selectedClient.medical_assistance_flag || false
        };
        await api.clients.update(selectedClient.client_id, updateData);
      } else {
        // Create new client - use the appropriate provider
        const access = await getPortalAccess();
        const currentUser = await authService.getCurrentUser();
        const providerId = resolveProviderId(
          access,
          selectedProvider,
          currentUser.attributes['custom:provider_id'],
        );
        
        if (!providerId) {
          throw new Error('No provider selected');
        }
        
        const clientData: ClientCreate = {
          provider_id: providerId,
          first_name: selectedClient.first_name,
          last_name: selectedClient.last_name,
          medicaid_num: selectedClient.medicaid_num,
          first_initial: selectedClient.first_initial,
          date_of_birth: selectedClient.date_of_birth,
          gender: selectedClient.gender,
          phone: selectedClient.phone,
          email: selectedClient.email,
          address_line1: selectedClient.address_line1,
          address_line2: selectedClient.address_line2,
          city: selectedClient.city,
          county_id: selectedClient.county_id,
          state: selectedClient.state,
          zip_code: selectedClient.zip_code,
          emergency_contact_name: selectedClient.emergency_contact_name,
          emergency_contact_phone: selectedClient.emergency_contact_phone,
          goals: selectedClient.goals || {},
          personal_care_plan: selectedClient.personal_care_plan || {},
          allergies: selectedClient.allergies,
          notes: selectedClient.notes,
          authorized_service_codes: selectedClient.authorized_service_codes || [],
          isp: selectedClient.isp || '',
          favorite_locations: selectedClient.favorite_locations || {},
          complex_care_flag: selectedClient.complex_care_flag || false,
          behavioral_support_flag: selectedClient.behavioral_support_flag || false,
          medical_assistance_flag: selectedClient.medical_assistance_flag || false
        };
        await api.clients.create(clientData);
      }
      setIsDialogOpen(false);
      fetchClients();
    } catch (err) {
      console.error('Error saving client:', err);
      setError('Failed to save client. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;

    try {
      setIsLoading(true);
      setError(null);
      await api.clients.delete(selectedClient.client_id);
      setIsDeleteDialogOpen(false);
      fetchClients();
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    setIsDialogOpen(false);
    setSelectedClient(null);
    setClientSaveAttempted(false);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleDialogBackdropClick = (event: object, reason: string) => {
    if (reason === 'backdropClick') {
      handleCloseDialog();
    }
  };

  const handleAddNew = async () => {
    setClientSaveAttempted(false);
    try {
      const access = await getPortalAccess();
      const currentUser = await authService.getCurrentUser();
      const providerId = resolveProviderId(
        access,
        selectedProvider,
        currentUser.attributes['custom:provider_id'],
      );

      if (access.requiresProviderSelection && !selectedProvider) {
        setError('Please select a provider first to add a new client.');
        return;
      }
      if (!providerId && !access.isAdmin) {
        setError('Please select a provider first to add a new client.');
        return;
      }
      
      setSelectedClient({
        client_id: '',
        provider_id: providerId ?? selectedProvider,
        first_name: '',
        last_name: '',
        medicaid_num: undefined,
        first_initial: '',
        date_of_birth: '',
        gender: '',
        phone: '',
        email: '',
        address_line1: '',
        address_line2: '',
        city: '',
        county_id: undefined,
        state: '',
        zip_code: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        goals: {},
        personal_care_plan: {},
        allergies: '',
        notes: '',
        authorized_service_codes: [],
        isp: '',
        favorite_locations: {},
        complex_care_flag: false,
        behavioral_support_flag: false,
        medical_assistance_flag: false
      } as any);
      setIsDialogOpen(true);
    } catch (err) {
      console.error('Error creating new client:', err);
      setError('Failed to create new client. Please try again.');
    }
  };

  const providerName = (provider: Provider) =>
    `${provider.first_name} ${provider.last_name}`.trim();

  const clientName = (client: Client) =>
    `${client.first_name} ${client.last_name}`.trim();

  const providerOptions = [...providers].sort((a, b) =>
    providerName(a).localeCompare(providerName(b), undefined, { sensitivity: 'base' })
  );

  const providerQuery = providerFilterInput.trim().toLowerCase();
  const clientQuery = clientFilterInput.trim().toLowerCase();

  const clientMatchesProvider = (client: Client) => {
    if (providerFilter) {
      return String(client.provider_id) === String(providerFilter.provider_id);
    }
    if (!providerQuery) return true;
    const provider = providers.find((item) => String(item.provider_id) === String(client.provider_id));
    return !!provider && providerName(provider).toLowerCase().includes(providerQuery);
  };

  const clientOptions = clients
    .filter(clientMatchesProvider)
    .sort((a, b) => clientName(a).localeCompare(clientName(b), undefined, { sensitivity: 'base' }));

  const visibleClients = clientOptions.filter((client) => {
    if (clientFilter) {
      return String(client.client_id) === String(clientFilter.client_id);
    }
    if (!clientQuery) return true;
    return clientName(client).toLowerCase().includes(clientQuery);
  });

  const filtersActive = Boolean(
    providerFilter || providerFilterInput.trim() || clientFilter || clientFilterInput.trim()
  );

  const clearFilters = () => {
    setProviderFilter(null);
    setProviderFilterInput('');
    setClientFilter(null);
    setClientFilterInput('');
  };

  const selectedClientOption = clientFilter && clientOptions.some(
    (client) => String(client.client_id) === String(clientFilter.client_id)
  )
    ? clientFilter
    : null;

  if (isLoading && clients.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Clients
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchClients}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter Clients
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={providerOptions}
                value={providerFilter}
                inputValue={providerFilterInput}
                onChange={(_, newValue) => {
                  setProviderFilter(newValue);
                  if (
                    newValue &&
                    clientFilter &&
                    String(clientFilter.provider_id) !== String(newValue.provider_id)
                  ) {
                    setClientFilter(null);
                    setClientFilterInput('');
                  }
                }}
                onInputChange={(_, value, reason) => {
                  setProviderFilterInput(value);
                  if (reason === 'input' || reason === 'clear') {
                    setProviderFilter(null);
                    setClientFilter(null);
                    setClientFilterInput('');
                  }
                }}
                getOptionLabel={providerName}
                isOptionEqualToValue={(option, value) =>
                  String(option.provider_id) === String(value.provider_id)
                }
                noOptionsText="No matching providers"
                componentsProps={{ paper: { sx: { bgcolor: '#ffffff' } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Provider"
                    placeholder="Type a name..."
                    sx={fieldOnPageBlueSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={clientOptions}
                value={selectedClientOption}
                inputValue={clientFilterInput}
                onChange={(_, newValue) => setClientFilter(newValue)}
                onInputChange={(_, value, reason) => {
                  setClientFilterInput(value);
                  if (reason === 'input' || reason === 'clear') {
                    setClientFilter(null);
                  }
                }}
                getOptionLabel={clientName}
                isOptionEqualToValue={(option, value) =>
                  String(option.client_id) === String(value.client_id)
                }
                noOptionsText="No matching clients"
                componentsProps={{ paper: { sx: { bgcolor: '#ffffff' } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Client"
                    placeholder="Type a name..."
                    sx={fieldOnPageBlueSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={clearFilters}
                sx={{ mt: 1, backgroundColor: '#2D4B73', '&:hover': { backgroundColor: '#1E3452' } }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {visibleClients.length === 0 ? (
        <Alert severity="info">
          {filtersActive
            ? 'No clients match this search.'
            : (isAdmin || requiresProviderSelection)
              ? 'No clients found for the providers you can access.'
              : 'No clients found for your assigned provider. Add a new client to get started.'
          }
        </Alert>
      ) : (
        <Paper sx={{ 
          borderRadius: 0,
          backgroundColor: '#ffffff',
          border: '1px solid rgba(45, 75, 115, 0.2)',
          boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
        }}>
          <List>
            {visibleClients.map((client) => (
              <ListItem
                key={client.client_id}
                button
                onClick={() => {
                  setClientSaveAttempted(false);
                  setSelectedClient(client);
                  setIsDialogOpen(true);
                }}
              >
                <ListItemText
                  primary={`${client.first_name} ${client.last_name}`}
                  secondary={`DOB: ${client.date_of_birth}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClient(client);
                      setIsDeleteDialogOpen(true);
                    }}
                    disabled={isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}



      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 80, right: 16 }}
        onClick={handleAddNew}
        disabled={isLoading || ((isAdmin || requiresProviderSelection) && !selectedProvider)}
      >
        <AddIcon />
      </Fab>

      <Dialog open={isDialogOpen} onClose={handleDialogBackdropClick} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedClient?.client_id ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                value={selectedClient?.first_name || ''}
                error={clientSaveAttempted && !selectedClient?.first_name?.trim()}
                helperText={
                  clientSaveAttempted && !selectedClient?.first_name?.trim() ? 'First name is required' : undefined
                }
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, first_name: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                value={selectedClient?.last_name || ''}
                error={clientSaveAttempted && !selectedClient?.last_name?.trim()}
                helperText={
                  clientSaveAttempted && !selectedClient?.last_name?.trim() ? 'Last name is required' : undefined
                }
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, last_name: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Medicaid Number"
                type="number"
                value={selectedClient?.medicaid_num || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, medicaid_num: e.target.value ? Number(e.target.value) : undefined } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Initial"
                value={selectedClient?.first_initial || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, first_initial: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AppDateField
                label="Date of Birth"
                value={selectedClient?.date_of_birth || null}
                onChange={v =>
                  setSelectedClient(prev =>
                    prev ? { ...prev, date_of_birth: v || '' } : null
                  )
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Gender"
                value={selectedClient?.gender || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, gender: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={selectedClient?.phone || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, phone: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={selectedClient?.email || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, email: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <AddressFields
                disabled={isLoading}
                value={{
                  address_line1: selectedClient?.address_line1 || '',
                  address_line2: selectedClient?.address_line2 || '',
                  city: selectedClient?.city || '',
                  state: selectedClient?.state || '',
                  zip_code: selectedClient?.zip_code || '',
                }}
                onChange={(next: AddressValue) =>
                  setSelectedClient(prev =>
                    prev
                      ? {
                          ...prev,
                          address_line1: next.address_line1,
                          address_line2: next.address_line2,
                          city: next.city,
                          state: next.state,
                          zip_code: next.zip_code,
                        }
                      : null
                  )
                }
                afterCity={
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>County</InputLabel>
                      <Select
                        value={selectedClient?.county_id || ''}
                        label="County"
                        onChange={e => {
                          const countyId = e.target.value ? Number(e.target.value) : undefined;
                          setSelectedClient(prev => (prev ? { ...prev, county_id: countyId } : null));
                        }}
                        disabled={isLoading}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {ohioCounties.map(county => (
                          <MenuItem key={county.county_id} value={county.county_id}>
                            {county.county_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={selectedClient?.emergency_contact_name || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, emergency_contact_name: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={selectedClient?.emergency_contact_phone || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, emergency_contact_phone: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Allergies"
                value={selectedClient?.allergies || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, allergies: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={selectedClient?.notes || ''}
                onChange={(e) =>
                  setSelectedClient(prev => prev ? { ...prev, notes: e.target.value } : null)
                }
                disabled={isLoading}
              />
            </Grid>

            {/* Rate Modifiers Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Rate Modifiers
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedClient?.complex_care_flag || false}
                    onChange={(e) =>
                      setSelectedClient(prev => prev ? { ...prev, complex_care_flag: e.target.checked } : null)
                    }
                    disabled={isLoading}
                  />
                }
                label="Complex Care"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedClient?.behavioral_support_flag || false}
                    onChange={(e) =>
                      setSelectedClient(prev => prev ? { ...prev, behavioral_support_flag: e.target.checked } : null)
                    }
                    disabled={isLoading}
                  />
                }
                label="Behavioral Support"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedClient?.medical_assistance_flag || false}
                    onChange={(e) =>
                      setSelectedClient(prev => prev ? { ...prev, medical_assistance_flag: e.target.checked } : null)
                    }
                    disabled={isLoading}
                  />
                }
                label="Medical Assistance"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedClient?.first_name} {selectedClient?.last_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelClose}>
        <DialogTitle>Abandon Client?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to abandon this client entry? All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Cancel</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Abandon
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients; 